"use client";

import React from "react";
import Link from "next/link";
import Icon from "@/components/ui/Icon";
import BlogTableOfContents from "./BlogTableOfContents";
import { BlogPost } from "@/data/blogPosts";
import { COMPANY_INFO } from "@/data/companyInfo";

interface BlogSidebarProps {
  headings: { id: string; title: string }[];
  relatedPosts: BlogPost[];
}

export default function BlogSidebar({ headings, relatedPosts }: BlogSidebarProps) {
  return (
    <div className="space-y-6">
      {/* 1. Table of Contents */}
      {headings.length >= 2 && <BlogTableOfContents headings={headings} />}

      {/* 2. Follow Us Widget matching user reference */}
      <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low/80 dark:bg-[#25201D]/90 backdrop-blur-md p-4 sm:p-5 shadow-sm">
        <h3 className="font-headline-md text-xs sm:text-sm font-bold text-on-surface uppercase tracking-wider mb-3.5 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          Follow Us
        </h3>

        <div className="flex items-center gap-2.5">
          {/* LinkedIn */}
          <a
            href={COMPANY_INFO.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Beyond Ambition on LinkedIn"
            className="w-10 h-10 rounded-xl bg-surface hover:bg-[#0A66C2] text-on-surface hover:text-white border border-outline-variant/30 flex items-center justify-center transition-all duration-200 shadow-2xs hover:scale-105 group"
          >
            <svg
              className="w-4 h-4 fill-current"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.45a1.64 1.64 0 0 0-1.66 1.64 1.65 1.65 0 0 0 1.66 1.64 1.64 1.64 0 0 0 1.64-1.64c0-.9-.74-1.64-1.64-1.64Z" />
            </svg>
          </a>

          {/* X / Twitter */}
          <a
            href="https://x.com/beyondambition"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Beyond Ambition on X"
            className="w-10 h-10 rounded-xl bg-surface hover:bg-black text-on-surface hover:text-white border border-outline-variant/30 flex items-center justify-center transition-all duration-200 shadow-2xs hover:scale-105"
          >
            <svg
              className="w-3.5 h-3.5 fill-current"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>

          {/* GitHub */}
          <a
            href={COMPANY_INFO.github}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Beyond Ambition on GitHub"
            className="w-10 h-10 rounded-xl bg-surface hover:bg-[#24292e] text-on-surface hover:text-white border border-outline-variant/30 flex items-center justify-center transition-all duration-200 shadow-2xs hover:scale-105"
          >
            <svg
              className="w-4 h-4 fill-current"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.1-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2Z" />
            </svg>
          </a>

          {/* WhatsApp */}
          <a
            href={COMPANY_INFO.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Message Beyond Ambition on WhatsApp"
            className="w-10 h-10 rounded-xl bg-surface hover:bg-[#25D366] text-on-surface hover:text-white border border-outline-variant/30 flex items-center justify-center transition-all duration-200 shadow-2xs hover:scale-105"
          >
            <svg
              className="w-4 h-4 fill-current"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.42a8.23 8.23 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24m4.52 11.66c-.25-.13-1.47-.72-1.7-.81-.23-.08-.39-.13-.56.13-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.13-1.06-.39-2.01-1.24-.74-.66-1.24-1.48-1.39-1.73-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.44-.06-.13-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.47c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1s.9 2.44 1.03 2.61c.13.17 1.77 2.7 4.29 3.79.6.26 1.07.41 1.43.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.1-.23-.17-.48-.29" />
            </svg>
          </a>

          {/* Email */}
          <a
            href={`mailto:${COMPANY_INFO.email}`}
            aria-label="Email Beyond Ambition Engineering"
            className="w-10 h-10 rounded-xl bg-surface hover:bg-primary text-on-surface hover:text-white border border-outline-variant/30 flex items-center justify-center transition-all duration-200 shadow-2xs hover:scale-105"
          >
            <Icon name="mail" size={17} />
          </a>
        </div>
      </div>

      {/* 3. The Latest / Related Research matching user reference */}
      {relatedPosts && relatedPosts.length > 0 && (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low/80 dark:bg-[#25201D]/90 backdrop-blur-md p-4 sm:p-5 shadow-sm space-y-3.5">
          <div className="flex items-center justify-between border-b border-outline-variant/20 pb-3">
            <h3 className="font-headline-md text-xs sm:text-sm font-bold text-on-surface uppercase tracking-wider flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              The Latest
            </h3>
            <Link
              href="/blog"
              className="text-[11px] font-bold text-primary hover:underline flex items-center gap-0.5"
            >
              <span>View All</span>
              <Icon name="arrow_forward" size={12} />
            </Link>
          </div>

          <div className="space-y-2.5">
            {relatedPosts.slice(0, 4).map((rel) => (
              <Link
                key={rel.slug}
                href={`/blog/${rel.slug}`}
                className="block p-3 rounded-xl border border-outline-variant/25 bg-surface/70 dark:bg-[#1E1916]/70 hover:bg-surface-container/90 dark:hover:bg-[#2B2420] hover:border-primary/40 transition-all duration-200 group shadow-2xs"
              >
                <span className="inline-block text-[10px] font-label-md text-primary font-bold uppercase tracking-wider mb-1">
                  {rel.category}
                </span>
                <h4 className="font-headline-md text-xs sm:text-[13px] font-bold text-on-surface line-clamp-2 group-hover:text-primary transition-colors leading-snug mb-1.5">
                  {rel.title}
                </h4>
                <div className="flex items-center gap-2 text-[11px] text-on-surface-variant/80 font-medium">
                  <span>{rel.date}</span>
                  <span className="opacity-50">—</span>
                  <span>{rel.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
